import { formatDate, formatDate2, formatBirthDate } from "./DateFormat";

export const formatPhone = (value) => {
  let result = null
  try {
    result = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`
  } catch (e) { }
  return result
}

export function formatPatientStatus(status) {
  switch (status) {
    case "Disabled":
      return "Deactivated";
    case "Enabled":
      return "Activated";
    default:
      return "";
    //      throw new Error(
    //        `Unregistered status "${status}" found in patient record`,
    //      );
  }
}

export const formatPatient = (data) => {
  return {
    id: data?.id,
    name: data?.name?.replaceAll("-", "") || "",
    firstName: data?.firstName || "-",
    lastName: data?.lastName || "-",
    userName: data?.username || "",
    gender: data?.gender || "-",
    phoneNumber: formatPhone(data?.phoneNumber) || "-",
    country: data?.country || "-",
    zipCode: data?.zipCode || "-",
    dosespotId: data?.dosespotId || "",
    cognitoId: data?.cognitoId || "",
    memberShipPlan: data?.membership?.name || "-",
    streetAddress: data?.address || "-",
    state: data?.state?.name || "- - -",
    stateId: data?.state?.id || "",
    DOB: formatBirthDate(data?.dateOfBirth) || "-",
    image: data?.image || "",
    actionStatus: formatPatientStatus(data?.status) || "",
    city: data?.city || "-",
    address1: data?.address1 || "-",
    address2: data?.address2 || "-",
    prefix: data?.prefix || "-",
    suffix: data?.suffix || "-",
    middleName: data?.middleName || "-",
    phoneType: data?.primaryPhoneType || "-",
  };
}

export const patientFormatterList = (data) => {
  const formattedResult = data.map((item) => {
    return formatPatient(item)
  });
  return formattedResult;
};

export const patientMedicationsFormatter = (data) => {
  const formattedResult = data.map((list) => {
    return {
      id: list?.id,
      // medicationId is a backend-hydrated object; guard the whole chain — a
      // deleted catalogue row must not take down the tab
      medicineName: list?.medicationId?.name || "",
      dosage: list?.dosage || "",
      medicineUsages: list?.frequency || "",
    };
  });
  return formattedResult;
};

// The medicalhistory payload merges two shapes per section: PREDEFINED
// catalogue entries (every active one, each with `selected: true|false`) and
// SELF-DEFINED patient entries (no `selected` key at all). Only predefined
// entries the patient actually selected — plus everything self-defined —
// belong to the patient.
const belongsToPatient = (item) =>
  item?.selected === true || item?.selected === undefined;

export const getMedicalHistoryFormatter = ({
  bmi,
  alcohol,
  tobacco,
  sexualHistory,
  userAllergies,
  familyHistory,
  userMedicalconditions,
  userImmunization,
  userSurgical,
}) => {
  const formatFamilyHistory = (familyHistory) => {
    return familyHistory
      ?.filter(belongsToPatient)
      .map((history) => history?.name)
      .filter(Boolean);
  };
  const formatMedicalConditions = (conditions) => {
    return (conditions || [])
      .filter(belongsToPatient)
      .map((condition) => condition?.name)
      .filter(Boolean);
  };
  const formatUserAllergy = (userAllergy) => {
    return userAllergy?.map((allergy) => {
      return {
        name: allergy?.allergyId?.name || "-",
        type: allergy?.allergyId?.type || "-",
        reaction: allergy?.reaction || "-",
      };
    });
  };
  const formatUserImmunization = (userImmunization) => {
    const formatImmunizationForm = (immunizations) => {
      return immunizations
        ?.filter(
          (immunization) =>
            immunization?.selected === true ||
            immunization?.children?.some((child) => child?.selected),
        )
        .map((immunization) => {
          return {
            id: immunization.id,
            name: immunization.name,
            subName: immunization?.children
              ?.filter((child) => child?.selected)
              .map((child) => ({ id: child?.id, name: child.name })),
          };
        });
    };
    // patient-typed immunizations (custom) belong in the list too
    const custom = (userImmunization?.custom || []).map((immunization) => ({
      id: immunization?.id,
      name: immunization?.name,
      subName: [],
    }));
    return {
      id: userImmunization?.id,
      immuniztionName: [
        ...(formatImmunizationForm(userImmunization?.immunizationForm) || []),
        ...custom,
      ],
    };
  };
  const formatUserSurgical = (userSurgical) => {
    // predefined selections live in surgicalForm (with `selected`); custom
    // holds the patient-typed ones — the section previously showed only custom
    const predefined = (userSurgical?.surgicalForm || [])
      .filter((surgery) => surgery?.selected === true)
      .map((surgery) => ({ name: surgery?.name ?? "" }));
    const custom = (userSurgical?.custom || []).map((surgery) => ({
      name: surgery?.name ?? "",
    }));
    return [...predefined, ...custom];
  };
  return {
    heightFeet: bmi?.feet ?? "-",
    heightInches: bmi?.inches ?? "-",
    weight: bmi?.weightLbs ?? "-",
    allergies: formatUserAllergy(userAllergies) || "-",
    alcoholConsumption: alcohol || "-",
    tobacoPerWeek: tobacco?.tobaccoPerWeek || "",
    tobacoAnnual: tobacco?.tobaccoYears || "",
    medicalConditions: formatMedicalConditions(userMedicalconditions),
    familyHistory: formatFamilyHistory(familyHistory) || "-",
    userImmunization: formatUserImmunization(userImmunization) || "",
    userSurgical: formatUserSurgical(userSurgical) || "",
    sexualHistory: sexualHistory ? "Yes" : "No",
  };
};

export const patientVisitsFormatter = (data) => (
  data.map((item) => ({
    id: item?._id,
    status: item.status,
    states: item.states,
    offerActiveMinutes: item.offerActiveMinutes ? `${item.offerActiveMinutes} mins` : 'N/A',
    docName: item?.doctorFirstName && item?.doctorLastName ? `${item.doctorFirstName} ${item.doctorLastName}` : 'None',
    doctorUsername: item?.doctorUsername || '',
    createdAt: formatDate2(item?.createdAt),
    startedAt: formatDate2(item?.startedAt),
    endedAt: formatDate2(item?.endedAt),
    waitStartedAt: formatDate2(item?.waitStartedAt),
    waitEndedAt: formatDate2(item?.waitEndedAt),
    lastMessage: item?.lastMessage,
  }))
)
